import React, { useCallback, useEffect, useState } from "react";
import { StudioLayout } from "../components/layout/StudioLayout";
import { BeginnerPathPhaseShell } from "../components/beginnerPath/BeginnerPathPhaseShell";
import { GymLibrary } from "../components/gym/GymLibrary";
import { GymExerciseProvider } from "../components/gym/GymExerciseSession";
import { GymExerciseStudio } from "../components/gym/GymExerciseStudio";
import { getExerciseById } from "../lib/gym/trainingPlan";
import { applyPracticeStreak } from "../lib/gym/progressMetrics";
import { useBeginnerPathEntry } from "../hooks/useBeginnerPathEntry";
import type { UserProgress } from "../lib/gym/types";

const STORAGE_KEY = "bordona-gym-progress";

const defaultProgress: UserProgress = {
  completedExercises: [],
  currentExercise: null,
  scores: {},
  totalPracticeTime: 0,
  streakDays: 0,
  lastPracticeDate: null,
};

function loadProgress(): UserProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultProgress;
    const p = JSON.parse(raw) as Partial<UserProgress>;
    return {
      ...defaultProgress,
      ...p,
      completedExercises: Array.isArray(p.completedExercises) ? p.completedExercises : [],
      scores: p.scores && typeof p.scores === "object" ? p.scores : {},
      totalPracticeTime: typeof p.totalPracticeTime === "number" ? p.totalPracticeTime : 0,
      streakDays: typeof p.streakDays === "number" ? p.streakDays : 0,
    };
  } catch {
    return defaultProgress;
  }
}

export const BordonaGymPage: React.FC = () => {
  const { fromPath, phase, gymFocusLevelId } = useBeginnerPathEntry();
  const [activeExercise, setActiveExercise] = useState<string | null>(null);
  const [progress, setProgress] = useState<UserProgress>(loadProgress);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch {
      /* ignore quota */
    }
  }, [progress]);

  const handleSelectExercise = useCallback((id: string) => {
    setActiveExercise(id);
    setProgress((p) => {
      const { streakDays, lastPracticeDate } = applyPracticeStreak(p.streakDays, p.lastPracticeDate);
      return {
        ...p,
        currentExercise: id,
        streakDays,
        lastPracticeDate,
      };
    });
  }, []);

  const handleBackToLibrary = useCallback(() => {
    setActiveExercise(null);
    setProgress((p) => ({ ...p, currentExercise: null }));
  }, []);

  const recordPracticeSeconds = useCallback((seconds: number) => {
    if (seconds <= 0) return;
    setProgress((p) => ({
      ...p,
      totalPracticeTime: p.totalPracticeTime + seconds,
    }));
  }, []);

  const handleComplete = useCallback((exerciseId: string, score: number) => {
    setProgress((p) => {
      const { streakDays, lastPracticeDate } = applyPracticeStreak(p.streakDays, p.lastPracticeDate);
      const newCompleted = p.completedExercises.includes(exerciseId)
        ? p.completedExercises
        : [...p.completedExercises, exerciseId];
      return {
        ...p,
        completedExercises: newCompleted,
        scores: { ...p.scores, [exerciseId]: score },
        streakDays,
        lastPracticeDate,
      };
    });
  }, []);

  const activeExerciseData = activeExercise ? getExerciseById(activeExercise) : null;
  const pathMode = fromPath && !!phase;

  const gymContent = activeExerciseData ? (
    <GymExerciseProvider
      key={activeExerciseData.id}
      exercise={activeExerciseData}
      progress={progress}
      onComplete={handleComplete}
      onAccumulatePracticeSeconds={recordPracticeSeconds}
      pathMode={pathMode}
    >
      <GymExerciseStudio onBack={handleBackToLibrary} pathMode={pathMode} />
    </GymExerciseProvider>
  ) : (
    <GymLibrary
      progress={progress}
      onSelectExercise={handleSelectExercise}
      pathMode={pathMode}
      focusLevelId={gymFocusLevelId}
    />
  );

  if (pathMode && phase) {
    return (
      <BeginnerPathPhaseShell phase={phase} fullBleed={!!activeExerciseData}>
        {gymContent}
      </BeginnerPathPhaseShell>
    );
  }

  return (
    <StudioLayout hideAppBar={!!activeExerciseData}>{gymContent}</StudioLayout>
  );
};

export default BordonaGymPage;
